import { NetworkStatus, useReactiveVar } from "@apollo/client";
import {
  useFocusEffect,
  useLocalSearchParams,
  usePathname,
  useRouter,
} from "expo-router";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { BackHandler } from "react-native";

import MainView from "./MainView";
import { showActionsheetVar } from "../../stores/showActionsheet";

import { CustomPromptRefProps } from "@/components/molecules/CustomPrompt/CustomPromptContainer";
import { AnalyticsTracker } from "@/features/analytics";
import { useAuth } from "@/features/auth";
import { filterValuesVar } from "@/features/player-filter";
import {
  useLogPlayerProfileViewMutation,
  useRosterManagementListMyOrganizationsPlayersQuery,
  useWatchlistListMyUserWatchlistsQuery,
} from "@/generated/graphql";
import { MY_WATCHLISTS_PAGE_SIZE } from "@/features/watchlist/constants";
import useCreateWatchlistActions from "@/hooks/useCreateWatchlistActions";
import { useCustomMetricSortVariables } from "@/hooks/useCustomMetricSortVariables";
import useMobileScreen from "@/hooks/useMobileScreen";
import { usePlayerDetailQuery } from "@/hooks/usePlayerDetailQuery";
import { useRecentPlayers } from "@/hooks/useRecentPlayers";
import useSetPageTitle from "@/hooks/useSetPageTitle";
import { useToastNotification } from "@/hooks/useToastNotification";
import useWatchlistManagement from "@/hooks/useWatchlistManagement";
import { useNavigationContext } from "@/providers/NavigationProvider";
import { useCanAccessFeature } from "@/providers/FeatureFlags";
import { useTeamSelection } from "@/providers/TeamSelectionProvider";
import { useTheme } from "@/providers/ThemeProvider";
import { playerAutoCreateNoteVar, selectPlayerTab } from "@/stores/player";
import { watchlistSortVar } from "@/stores/selectionSort";

/**
 * Delay (ms) before logging a player profile view (SE-5204). Sized to land
 * just past the initial-render GraphQL fan-out idle window (the SE-5038 fan-out
 * gate stops counting after 3s of request silence), so this unbatched view-log
 * mutation does not count against the ≤3 initial-render round-trip budget. The
 * timer starts once the player's data is available — roughly when the last
 * initial-render request is sent — so this must stay above that 3s window.
 */
const PROFILE_VIEW_LOG_DEFER_MS = 4000;

/**
 * Container component for the player detail main screen with chat functionality.
 */
const ChatContainer = () => {
  const promptRef = useRef<CustomPromptRefProps>(
    null
  ) as React.RefObject<CustomPromptRefProps>;
  const router = useRouter();
  const pathname = usePathname();
  const showActionsheet = useReactiveVar(showActionsheetVar);
  const toast = useToastNotification();
  const isMobileScreen = useMobileScreen();
  const auth = useAuth();
  const sortValues = useReactiveVar(watchlistSortVar);
  const filterValues = useReactiveVar(filterValuesVar);
  const isSortPlayerEnable = useCanAccessFeature("globalSort");
  const {
    isKanbanNavigation,
    isSimilarPlayersNavigation,
    breadcrumbs,
    pushBreadcrumb,
    updateLastBreadcrumb,
  } = useNavigationContext();
  const { addRecentPlayer } = useRecentPlayers();
  const [logPlayerProfileView] = useLogPlayerProfileViewMutation();
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";

  // Compute sort variables matching the roster page so Apollo cache stays consistent
  const { sortVariables } = useCustomMetricSortVariables(
    sortValues,
    isSortPlayerEnable
  );

  const { isLoaded: isTeamSelectionLoaded } = useTeamSelection();

  const rosterQueryVariables = useMemo(
    () => ({
      ...sortVariables,
      positionIds: filterValues?.positions?.map?.(position => position.id),
    }),
    [sortVariables, filterValues]
  );

  // Force desktop layout when navigating from kanban, regardless of screen size
  const isSmallScreen = isKanbanNavigation ? false : isMobileScreen;

  const { handleAddWatchlistPress } = useCreateWatchlistActions({
    promptRefForModal: promptRef,
  });
  const { playerId, tab, action } = useLocalSearchParams<{
    playerId: string;
    tab?: string;
    action?: string;
  }>();

  // Query roster players for the left sidebar with sort/filter variables matching
  // the roster page so Apollo cache entries stay consistent across navigation
  const {
    data: playersData,
    fetchMore: fetchMorePlayers,
    refetch: refetchPlayers,
    loading: playersLoading,
    networkStatus: playersNetworkStatus,
  } = useRosterManagementListMyOrganizationsPlayersQuery({
    variables: rosterQueryVariables,
    notifyOnNetworkStatusChange: true,
    skip: !isTeamSelectionLoaded,
    // Never coalesce the roster list: a batched POST resolves only when its
    // SLOWEST member does, and this screen's boot fan-out fills the batch to
    // `BATCH_MAX` (10) with nine reference-data queries that only feed filter
    // dropdowns. When any one of them ran long the shared 25s
    // `BATCH_REQUEST_TIMEOUT_MS` aborted the whole POST, so the one query the
    // screen's content depends on never resolved — and because nothing
    // refires it, `/players` sat on its "No Players" empty state for the rest
    // of the session with no error and no retry. Unbatched it answers in
    // ~200ms and, if it does fail, fails alone where SE-5055's error+retry can
    // see it. Same rationale as `AuthMe` (SE-6233).
    context: { batch: false },
  });

  const { data: watchlistData } = useWatchlistListMyUserWatchlistsQuery({
    variables: { first: MY_WATCHLISTS_PAGE_SIZE },
    // Defer until team selection is hydrated. Without this, the query fires on
    // mount and double-subscribes alongside useWatchlistManagement below
    // (Edge 1) — gating both on the same condition prevents the premature fire.
    skip: !isTeamSelectionLoaded,
  });

  const {
    data,
    loading: playerDetailLoading,
    error: playerDetailError,
    refetch: refetchPlayerDetail,
  } = usePlayerDetailQuery({
    variables: { id: playerId ?? "" },
    // `playerId` is momentarily undefined/"" while the route mounts and tears
    // down despite its `string` type; the team-selection gate does not cover
    // that window, so the poll would fire with `id: ""` and be rejected by the
    // server (`Variable "$id" got invalid value ""` — SE-5638). Trimmed to
    // match the hook backstop: `/players/%20` yields a truthy-but-invalid " ".
    skip: !isTeamSelectionLoaded || !playerId?.trim(),
  });

  // Tracks an in-flight retry so the error CTA can show a busy state. We use a
  // local flag rather than `notifyOnNetworkStatusChange` because flipping the
  // query's `loading` true during refetch would make `hasError` (which gates
  // on `!loading`) go false and unmount the error surface mid-retry — the flag
  // keeps the error screen mounted with a spinning, disabled button (SE-5055).
  const [isRetrying, setIsRetrying] = useState(false);

  // Retry the player detail query from the error state's CTA. Failures are
  // swallowed because Apollo also surfaces them via `playerDetailError`, which
  // keeps the error state visible (SE-5055).
  const onRetry = useCallback(async () => {
    // Symmetry with the focus refetch below: `refetch()` bypasses `skip` (and
    // the hook backstop only gates the query), so never replay it without a
    // usable id — trimmed, since " " is truthy but invalid (SE-5638).
    if (!playerId?.trim()) return;
    setIsRetrying(true);
    try {
      await refetchPlayerDetail();
    } catch {
      // Swallowed: Apollo also surfaces the failure via `playerDetailError`,
      // which keeps the error state visible (SE-5055).
    } finally {
      setIsRetrying(false);
    }
  }, [playerId, refetchPlayerDetail]);

  // Use the shared watchlist management hook - this handles both roster and watchlist switching
  const {
    watchlists: _watchlists,
    watchlistPlayers,
    activeWatchlist,
    loadWatchlists,
    loadingPlayers: loadingWatchlistPlayers,
    isRefreshing: isWatchlistRefreshing,
    handleDragEnd,
    handleLoadMore: handleLoadMoreWatchlist,
    handleRefresh: handleRefreshWatchlist,
    handleRefreshWatchlist: handleRefreshWatchlistCallback,
  } = useWatchlistManagement({
    accessToken: auth?.data?.accessToken ?? undefined,
    sortValues,
  });

  const player = useMemo(() => data?.getPlayer, [data?.getPlayer]);

  // Compute roster players for left sidebar when no watchlist is selected
  const rosterPlayers = useMemo(
    () =>
      playersData?.listMyOrganizationsPlayers?.edges?.map(edge => edge?.node) ??
      [],
    [playersData?.listMyOrganizationsPlayers?.edges]
  );

  const allWatchlists = useMemo(
    () =>
      watchlistData?.listMyUserWatchlists?.edges?.map(
        edge => edge?.node?.watchlist
      ) ?? [],
    [watchlistData?.listMyUserWatchlists?.edges]
  );

  const isRosterRefreshing = playersNetworkStatus === NetworkStatus.refetch;

  const handleLoadMoreRoster = useCallback(() => {
    const pageInfo = playersData?.listMyOrganizationsPlayers?.pageInfo;
    const { hasNextPage, endCursor } = pageInfo ?? {};
    if (hasNextPage && !playersLoading) {
      fetchMorePlayers({
        variables: { ...rosterQueryVariables, after: endCursor },
      });
    }
  }, [playersData, fetchMorePlayers, playersLoading, rosterQueryVariables]);

  // Apollo's `refetch()` can bypass `skip` in some versions; gate the
  // imperative refetch to mirror the auto-skip on the underlying query.
  const handleRefreshRoster = useCallback(() => {
    if (!isTeamSelectionLoaded) return;
    refetchPlayers(rosterQueryVariables).catch(() => {});
  }, [isTeamSelectionLoaded, refetchPlayers, rosterQueryVariables]);

  // Use roster players if no watchlist selected, otherwise use watchlist players
  const displayPlayers = activeWatchlist ? watchlistPlayers : rosterPlayers;
  const displayLoading = activeWatchlist
    ? loadingWatchlistPlayers
    : playersLoading;
  const displayRefreshing = activeWatchlist
    ? isWatchlistRefreshing
    : isRosterRefreshing;
  const displayLoadMore = activeWatchlist
    ? handleLoadMoreWatchlist
    : handleLoadMoreRoster;
  const displayRefresh = activeWatchlist
    ? handleRefreshWatchlist
    : handleRefreshRoster;

  const handleAddPress = useCallback(() => showActionsheetVar(true), []);

  const handleClosePress = useCallback(() => showActionsheetVar(false), []);

  const playerName = player?.name ?? "Loading...";

  // Live-value refs for the focus refetch below. Reading the latest
  // `refetchPlayerDetail` / `isTeamSelectionLoaded` through refs lets the
  // focus callback keep empty deps (see the effect for why that matters).
  const refetchPlayerDetailRef = useRef(refetchPlayerDetail);
  const isTeamSelectionLoadedRef = useRef(isTeamSelectionLoaded);
  // `playerId` too: a blur→focus transition landing in the window where the
  // route param is cleared would otherwise replay the refetch with `id: ""`,
  // which `skip` cannot stop because `refetch()` bypasses it (SE-5638).
  const playerIdRef = useRef(playerId);
  // Skips the initial mount focus so the gated query owns the first fetch.
  const hasFocusedOnceRef = useRef(false);

  // Side effects
  useSetPageTitle(playerName, { hideOnMobile: true });

  // Keep the focus-refetch refs pointed at the latest render values.
  useEffect(() => {
    refetchPlayerDetailRef.current = refetchPlayerDetail;
    isTeamSelectionLoadedRef.current = isTeamSelectionLoaded;
    playerIdRef.current = playerId;
  });

  // Refetch player data when the screen RE-gains focus (e.g. returning from
  // the intake form). The gated `usePlayerDetailQuery` above already owns the
  // initial fetch once `isTeamSelectionLoaded` settles true, so this effect
  // must not fire on the first focus — doing so put a second, identical
  // `PlayerDetailGetPlayer({id})` on the wire on every player open (SE-5038
  // AC2). Two safeguards:
  //   1. `hasFocusedOnceRef` skips the initial mount focus entirely.
  //   2. The callback has empty deps and reads live values via refs, so
  //      React Navigation only re-runs it on genuine focus transitions — not
  //      on the render/`refetch`-identity churn during mount, which otherwise
  //      re-ran the old `[isTeamSelectionLoaded, refetchPlayerDetail]` effect
  //      and fired extra refetches.
  useFocusEffect(
    useCallback(() => {
      if (!hasFocusedOnceRef.current) {
        // Initial mount focus — the gated query performs the first fetch.
        hasFocusedOnceRef.current = true;
        return;
      }
      // Apollo's `refetch()` can bypass `skip`; mirror the roster refresh
      // guard so we never fire with a stale `null` teamId before hydration.
      if (!isTeamSelectionLoadedRef.current) return;
      if (!playerIdRef.current?.trim()) return;
      refetchPlayerDetailRef.current().catch(() => {});
    }, [])
  );

  useEffect(() => {
    const backAction = () => {
      if (pathname.startsWith("/search/players")) {
        router.dismissAll();
      } else {
        router.back();
      }
      return true;
    };

    const backHandler = BackHandler.addEventListener(
      "hardwareBackPress",
      backAction
    );

    return () => {
      backHandler.remove();
    };
  }, [router, pathname]);

  // Guard against null player data (e.g., navigating with an invalid player ID)
  useEffect(() => {
    if (data && !playerDetailLoading && !data.getPlayer) {
      toast.show({ action: "danger", description: "Player not found" });
      router.back();
    }
  }, [data, playerDetailLoading, toast, router]);

  // Navigate to Reports & Notes tab with auto-create when query params are set
  // (e.g. Add > Add Note flow navigates with ?tab=notes&action=create)
  useEffect(() => {
    if (tab === "notes") {
      // SE-8285: the ?tab=notes deep link is about the routed player.
      selectPlayerTab(playerId, "reportsNotes");
    }
    if (action === "create") {
      playerAutoCreateNoteVar(true);
    }
  }, [tab, action, playerId]);

  // Track player view for recent players
  useEffect(() => {
    if (player?.id && player?.name) {
      addRecentPlayer({
        id: player.id,
        name: player.name,
        firstName: player.firstName,
        lastName: player.lastName,
      });
    }
  }, [
    player?.id,
    player?.name,
    player?.firstName,
    player?.lastName,
    addRecentPlayer,
  ]);

  // SE-5204: log the profile view server-side so the Search "Recently Viewed"
  // section lists players the user actually viewed (not just actioned).
  //
  // Deferred off the initial-render path. Mutations are sent unbatched (see
  // providers/batchTerminalLink), so firing this while the page is loading adds
  // a separate GraphQL round-trip and regresses the SE-5038 ≤3 initial-render
  // fan-out budget. We wait until the player's data has loaded and the initial
  // fan-out has gone idle, then fire once. Fire-and-forget: a failed/slow log
  // never affects the page, and a quick bounce (< the defer delay) simply isn't
  // recorded as a view.
  useEffect(() => {
    if (!player?.id) return;
    const playerId = player.id;
    const timer = setTimeout(() => {
      void logPlayerProfileView({ variables: { playerId } }).catch(() => {
        // Intentionally ignored — view logging is best-effort.
      });
      // SE-4010: product-analytics mirror of the server-side view log. Shares
      // the SE-5204 defer so the PostHog capture also stays off the
      // initial-render path; fire-and-forget like the mutation above.
      void AnalyticsTracker.player.logPlayerProfileViewed({
        player_id: playerId,
      });
    }, PROFILE_VIEW_LOG_DEFER_MS);
    return () => clearTimeout(timer);
  }, [player?.id, logPlayerProfileView]);

  // SE-5025 / SE-4613: standalone-context breadcrumb. When the player page is
  // reached via direct URL or hard refresh (no upstream caller pushed a
  // breadcrumb trail, e.g. a shared deep link), seed the default
  // "Home / [Player name]" trail so the header always renders an origin crumb
  // instead of nothing. SE-4613 resolved the deep-link fallback origin to
  // "Home" (path "/"), matching the Home navigation convention. Entry points
  // that already push their own breadcrumbs (scouting, kanban, squad, home,
  // search, dashboard, recommendations, ask-gemini, shadow-team, contracts)
  // are preserved — the guard only fires when the stack is empty.
  useEffect(() => {
    if (!player?.id || !playerName || playerName === "Loading...") return;
    if (breadcrumbs.length > 0) return;
    pushBreadcrumb({ label: "Home", path: "/" });
    pushBreadcrumb({ label: playerName, path: pathname });
  }, [player?.id, playerName, pathname, breadcrumbs.length, pushBreadcrumb]);

  // SE-5369: the player page is the authority on its own name. A caller can
  // seed this page's breadcrumb from stale/in-flight data (e.g. the Global
  // Search preview pushing the previously-previewed player's name while the
  // new player's query is still loading), leaving the last crumb showing the
  // wrong — or a generic "Player" — label. Once this player's data has loaded
  // and matches the URL id, reconcile the last crumb when it points at THIS
  // player so the breadcrumb always reflects the player actually on screen.
  useEffect(() => {
    // player.id === playerId guarantees the detail query resolved for THIS
    // route, so playerName is the real name (never the "Loading..." default).
    if (!player?.id || player.id !== playerId || !playerName) return;
    const last = breadcrumbs[breadcrumbs.length - 1];
    if (!last) return;
    // Exact suffix match on the pathname (query/hash stripped) so a longer id
    // like "p10" can't be matched by playerId "p1" via substring containment.
    const lastPathname = last.path.split(/[?#]/)[0] ?? "";
    if (!lastPathname.endsWith(`/players/${playerId}`)) return;
    if (last.label === playerName) return;
    updateLastBreadcrumb({ label: playerName, path: last.path });
  }, [player?.id, playerName, playerId, breadcrumbs, updateLastBreadcrumb]);

  return (
    <MainView
      loading={false}
      playerLoading={playerDetailLoading}
      // `hasError` intentionally ignores whether partial/cached data exists:
      // we always show the error + retry surface when the query has errored and
      // isn't loading. This is a conscious choice — currently moot since the
      // query doesn't surface usable partial data on error, but if a future
      // errorPolicy change starts retaining cached data alongside an error, we
      // still want the error surface to win rather than silently serve stale data.
      hasError={Boolean(playerDetailError) && !playerDetailLoading}
      onRetry={onRetry}
      isRetrying={isRetrying}
      isPlayerInAnyWatchlist={Boolean(player?.isWatchedByCurrentUser)}
      player={player}
      isSmallScreen={isSmallScreen}
      isDark={isDark}
      onAddWatchlistPress={handleAddWatchlistPress}
      showActionsheet={showActionsheet}
      onAddPress={handleAddPress}
      onWatchlistActionsheetClosePress={handleClosePress}
      promptRef={promptRef}
      // Support both roster and watchlist switching
      watchlists={allWatchlists}
      watchlistPlayers={displayPlayers}
      selectedWatchlist={activeWatchlist}
      loadWatchlists={loadWatchlists}
      loadingPlayers={displayLoading}
      refreshing={displayRefreshing}
      onLoadMore={displayLoadMore}
      onRefresh={displayRefresh}
      onDragEnd={handleDragEnd}
      onRefreshWatchlist={handleRefreshWatchlistCallback}
      isGoBack={isSimilarPlayersNavigation}
    />
  );
};

export default ChatContainer;
